import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { 
  FaCheckCircle, 
  FaEnvelope, 
  FaExclamationCircle,
  FaClock,
  FaTag,
  FaArrowLeft,
  FaInfoCircle
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../components/Button";
import Card from "../../components/Card";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import { api } from "../../services/api";
import Badge from "../../components/Badge";

export default function EmailDetailView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchEmail() {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/admin/emails/${id}`);
        setEmail(response.email);
      } catch (err) {
        setError(err.message || "Failed to load email details");
        console.error("Error fetching email:", err);
      } finally {
        setLoading(false);
      }
    }
    
    if (id) {
      fetchEmail();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <LoadingSpinner className="w-12 h-12 mx-auto mb-4" />
          <p className="text-text/60 text-sm">Loading email details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="p-8 text-center border-l-4 border-l-red-500">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 mb-4">
            <FaExclamationCircle className="w-7 h-7 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-text mb-2">Unable to Load Email</h3>
          <p className="text-text/60 text-sm mb-6 max-w-md mx-auto">{error}</p>
          <Button
            onClick={() => navigate(-1)}
            variant="secondary"
            className="inline-flex items-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            Return to List
          </Button>
        </Card>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-surface mb-4">
            <FaEnvelope className="w-7 h-7 text-text/30" />
          </div>
          <h3 className="text-lg font-semibold text-text mb-2">Email Not Found</h3>
          <p className="text-text/60 text-sm mb-6">
            This email may have been deleted or doesn't exist.
          </p>
          <Button
            onClick={() => navigate(-1)}
            variant="secondary"
            className="inline-flex items-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            Return to List
          </Button>
        </Card>
      </div>
    );
  }

  const getStatusConfig = (status) => {
    const configs = {
      sent: {
        icon: FaCheckCircle,
        color: "text-emerald-600",
        bgColor: "bg-emerald-50",
        borderColor: "border-emerald-200",
        dotColor: "bg-emerald-500",
        label: "Successfully Delivered"
      },
      failed: {
        icon: FaExclamationCircle,
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        dotColor: "bg-red-500",
        label: "Delivery Failed"
      },
      pending: {
        icon: FaClock,
        color: "text-amber-600",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
        dotColor: "bg-amber-500",
        label: "Pending Delivery"
      }
    };
    return configs[status] || configs.pending;
  };

  const statusConfig = getStatusConfig(email.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
      {/* Header with Breadcrumb Style */}
      <div className="mb-8">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-4 text-text/60 hover:text-primary inline-flex items-center gap-2 text-sm"
        >
          <FaArrowLeft className="w-3.5 h-3.5" />
          Back to Emails
        </Button>
        <PageHeader 
          title="Email Details" 
          subtitle="View complete email information and delivery status"
        />
      </div>

      <div className="space-y-6">
        {/* Status Card */}
        <Card className={`border-l-4 ${statusConfig.borderColor.replace('border', 'border-l')} overflow-hidden`}>
          <div className={`${statusConfig.bgColor} p-5`}>
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full ${statusConfig.bgColor} border-2 ${statusConfig.borderColor} flex items-center justify-center`}>
                <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className={`font-semibold ${statusConfig.color}`}>
                    {statusConfig.label}
                  </h3>
                  <span className={`w-2 h-2 rounded-full ${statusConfig.dotColor} animate-pulse`}></span>
                </div>
                {email.sentAt && (
                  <p className="text-sm text-black/70">
                    {dayjs(email.sentAt).format("MMMM D, YYYY [at] h:mm A")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Main Email Content */}
        <Card className="overflow-hidden">
          {/* Email Header */}
          <div className="bg-gradient-to-r from-primary/5 to-transparent p-6 border-b border-border/50">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recipient */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FaEnvelope className="w-4 h-4 text-primary" />
                  </div>
                  <label className="text-xs font-semibold text-text/60 uppercase tracking-wide">
                    Recipient
                  </label>
                </div>
                <p className="text-base font-medium text-text break-all pl-10">
                  {email.toEmail || "—"}
                </p>
              </div>

              {/* Email Type */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FaTag className="w-4 h-4 text-primary" />
                  </div>
                  <label className="text-xs font-semibold text-text/60 uppercase tracking-wide">
                    Email Type
                  </label>
                </div>
                <div className="pl-10">
                  <Badge variant="default" className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                    {email.type?.replace(/_/g, " ").toUpperCase() || "GENERAL"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Subject Section */}
          <div className="p-6 border-b border-border/50">
            <div className="flex items-start gap-3">
              <div className="w-1 h-16 bg-primary rounded-full"></div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-text/50 uppercase tracking-wide mb-2">
                  Subject Line
                </label>
                <h2 className="text-xl sm:text-2xl font-bold text-text leading-tight break-words">
                  {email.subject || "No Subject Provided"}
                </h2>
              </div>
            </div>
          </div>

          {/* Email Body */}
          <div className="p-6">
            <label className="flex items-center gap-2 text-xs font-semibold text-text/50 uppercase tracking-wide mb-4">
              <FaInfoCircle className="w-3.5 h-3.5" />
              Message Content
            </label>
            <div className="relative">
              {/* Decorative corner accent */}
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-primary/30 rounded-tl"></div>
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-primary/30 rounded-br"></div>
              
              <div
                className="prose prose-sm sm:prose max-w-none bg-surface/30 border border-border/40 rounded-lg p-5 sm:p-6 max-h-[500px] overflow-y-auto shadow-inner"
                style={{ wordBreak: "break-word" }}
                dangerouslySetInnerHTML={{
                  __html: email.body || "<p class='text-text/40 italic text-center py-8'>No message content available</p>",
                }}
              />
            </div>
          </div>

          {/* Error Message Banner */}
          {email.errorMessage && (
            <div className="mx-6 mb-6">
              <div className="bg-red-50 border-l-4 border-l-red-500 rounded-r-lg p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <FaExclamationCircle className="w-4 h-4 text-red-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-red-900 mb-1">
                      Delivery Error Details
                    </h4>
                    <p className="text-sm text-red-700 leading-relaxed break-words">
                      {email.errorMessage}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer Metadata */}
          <div className="px-6 py-4 bg-surface/20 border-t border-border/30">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4 text-xs text-text/50">
                <div className="flex items-center gap-2">
                  <FaClock className="w-3.5 h-3.5" />
                  <span>
                    {dayjs(email.createdAt || email.sentAt).format("MMM D, YYYY [at] h:mm A")}
                  </span>
                </div>
              </div>
              {email._id && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-text/40">ID:</span>
                  <code className="font-mono bg-surface px-2.5 py-1 rounded border border-border/40 text-text/60">
                    {email._id.slice(-12)}
                  </code>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}